/**
 * Did a dispatched work order reach the people who will do it?
 *
 * Dispatching sends the order to the contractor named on it and to whoever the
 * site's "Who is told" rules name — by SMS, email and the mobile app. Each
 * message is a row in the platform's outbox with a status; the contractor
 * confirms receipt by opening the app alert, tapping the link in the SMS or
 * email, or by phone, which the office records. This module turns those into
 * one answer per order, in five steps, each with its own color:
 *
 *   nobody        — dispatched, but nobody was messaged            (red)
 *   not_delivered — every message failed or had nowhere to go     (red)
 *   sending       — messages queued, waiting for the app or SMS   (amber)
 *   delivered     — at least one message arrived; not confirmed   (blue)
 *   received      — the contractor or crew confirmed they have it (green)
 *
 * Only a dispatched order is judged. Once work has started the crew plainly
 * has it, so a later order shows "received" when one was recorded and
 * nothing otherwise; a draft or approved order has not been sent anywhere.
 */
import type { ReceiptVia, WorkOrder } from "./workOrders";

export type DeliveryState = "nobody" | "not_delivered" | "sending" | "delivered" | "received";

export const DELIVERY_LABEL: Record<DeliveryState, string> = {
  nobody: "Nobody told",
  not_delivered: "Not delivered",
  sending: "Sending",
  delivered: "Delivered",
  received: "Received",
};

/** What the office should do next, in each state. */
export const DELIVERY_HINT: Record<DeliveryState, string> = {
  nobody: "No contractor or rule to tell. Set a contractor, or call them.",
  not_delivered: "No message got through. Call the contractor.",
  sending: "Messages are on their way.",
  delivered: "Delivered. Waiting for the contractor to confirm.",
  received: "The contractor confirmed they have it.",
};

export const DELIVERY_TONE: Record<DeliveryState, string> = {
  nobody: "var(--red)",
  not_delivered: "var(--red)",
  sending: "var(--amber)",
  delivered: "var(--blue)",
  received: "var(--green)",
};

export const RECEIPT_VIA_LABEL: Record<ReceiptVia, string> = {
  app: "in the mobile app",
  link: "by the confirm link",
  phone: "by phone",
  email: "by email",
  in_person: "in person",
  other: "",
};

const ARRIVED = ["sent", "delivered", "read"];
const ON_THE_WAY = ["queued", "waiting"];

export function deliveryOf(wo: Pick<WorkOrder, "status" | "receivedAt" | "delivery">): DeliveryState | null {
  if (wo.receivedAt) return "received";
  if (wo.status !== "dispatched") return null;
  const c = wo.delivery ?? {};
  const n = (keys: string[]) => keys.reduce((t, k) => t + (c[k] ?? 0), 0);
  if (n(ARRIVED)) return "delivered";
  if (n(ON_THE_WAY)) return "sending";
  if (Object.values(c).some((x) => x > 0)) return "not_delivered";
  return "nobody";
}

/** The order of the steps, for the strip on the detail page. */
export const DELIVERY_STEPS = ["sending", "delivered", "received"] as const;
